import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Search, UserCircle2 } from "lucide-react";
import PlacesLogo from "@/components/places/PlacesLogo";
import { useContent } from "@/lib/content";
import { SITE as SITE_DEFAULT, NAV } from "@/data/site";
import { useLang } from "@/lib/lang";
import { LOCALES, translatePath } from "@/lib/locales";

export default function Header({ onOpenCommand }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, locale, t } = useLang();
  const { site: SITE = SITE_DEFAULT, branding = {} } = useContent();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  const changeLang = (short) => {
    // Find target locale — for PT default we pick pt-BR; for others we take the first match.
    const target = LOCALES.find((l) => l.short === short) || LOCALES[0];
    const next = translatePath(pathname, target.prefix);
    navigate(`${next}${search || ""}${hash || ""}`);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const whatsHref = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Olá Places, gostaria de saber mais.")}`;

  return (
    <motion.header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-white/75 border-b border-black/5" : "bg-transparent"}`}
      initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between gap-4" style={{ height: scrolled ? 64 : 88 }}>
        <Link to="/" data-testid="nav-logo" className="font-display text-2xl tracking-tighter flex items-center">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={branding.brand_name || "Places"} style={{ height: branding.logo_height || 40 }} className="object-contain" />
          ) : (
            <PlacesLogo colorMode="light" size="text-2xl" />
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink key={n.key} to={n.path} data-testid={`nav-${n.key}-link`} className={({ isActive }) => `text-sm font-medium link-underline whitespace-nowrap ${isActive ? "text-black underline underline-offset-8 decoration-accent-orange decoration-2" : "text-neutral-700"}`}>
              {t.nav[n.key]}
            </NavLink>
          ))}
          <NavLink
            to="/38-anos"
            data-testid="nav-38-anos-link"
            className={({ isActive }) => `font-serif-italic text-base tracking-tight link-underline whitespace-nowrap ${isActive ? "text-accent-orange underline underline-offset-8 decoration-accent-orange decoration-2" : "text-accent-orange/85 hover:text-accent-orange"}`}
            title="38 anos de história"
          >
            38 anos
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <button onClick={onOpenCommand} data-testid="search-trigger" aria-label="Buscar" className="flex items-center gap-1.5 px-2.5 py-1.5 border border-black/10 rounded-full text-xs text-neutral-600 hover:bg-black hover:text-white transition-colors">
            <Search className="w-3.5 h-3.5" /> <kbd className="text-[10px] bg-black/10 px-1.5 py-0.5 rounded">⌘K</kbd>
          </button>
          <select value={lang} onChange={(e) => changeLang(e.target.value)} data-testid="lang-selector" className="text-xs font-medium bg-transparent border border-black/10 rounded-full px-2 py-1.5 cursor-pointer">
            <option value="pt">PT</option><option value="en">EN</option><option value="es">ES</option>
          </select>
          <a href={whatsHref} target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp" className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 border border-black/10 rounded-full hover:bg-accent-green hover:border-transparent transition whitespace-nowrap">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </a>
          <Link to="/contato?intent=client" data-testid="header-cta-client" className="bg-[#D4FF00] text-black text-xs font-semibold px-3 py-1.5 hover:bg-black hover:text-white transition rounded-full whitespace-nowrap">
            {t.cta.client}
          </Link>
          <Link to="/contato?intent=partner" data-testid="header-cta-partner" className="bg-black text-white text-xs font-semibold px-3 py-1.5 hover:bg-accent-orange transition rounded-full whitespace-nowrap">
            {t.cta.partner}
          </Link>
          <Link to="/cliente" data-testid="header-cta-cliente" className="flex items-center gap-1 bg-accent-orange text-white text-xs font-semibold px-3 py-1.5 hover:bg-black hover:text-accent-orange border border-accent-orange transition rounded-full whitespace-nowrap">
            <UserCircle2 className="w-3.5 h-3.5" /> {t.cta.clientArea || "Área do Cliente"}
          </Link>
        </div>

        <button className="lg:hidden p-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-trigger" aria-label="Menu">
          <Menu className="w-6 h-6" />
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black text-white p-5 flex flex-col overflow-hidden"
            style={{ minHeight: "100vh", minHeight: "100dvh" }}
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-6 shrink-0">
              <PlacesLogo colorMode="dark" size="text-xl" />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X className="w-7 h-7" /></button>
            </div>
            <nav className="flex flex-col gap-3 text-2xl font-display shrink-0">
              {NAV.map((n, i) => (
                <motion.div key={n.key} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
                  <NavLink to={n.path} data-testid={`mobile-nav-${n.key}`} onClick={() => setMobileOpen(false)} className={({ isActive }) => `inline-block ${isActive ? "underline underline-offset-8 decoration-accent-orange decoration-2" : ""}`}>{t.nav[n.key]}</NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * NAV.length }} className="pt-3 mt-1 border-t border-white/10">
                <NavLink to="/38-anos" data-testid="mobile-nav-38-anos" onClick={() => setMobileOpen(false)} className={({ isActive }) => `inline-block font-serif-italic text-accent-orange text-xl ${isActive ? "underline underline-offset-8 decoration-accent-orange decoration-2" : ""}`}>38 anos</NavLink>
              </motion.div>
            </nav>
            <div className="mt-4 flex flex-col gap-2 shrink-0">
              <a href={whatsHref} className="bg-accent-green text-black px-4 py-2.5 text-center text-sm font-semibold rounded-full hover:bg-accent-yellow transition-colors">WhatsApp</a>
              <Link to="/contato?intent=client" onClick={() => setMobileOpen(false)} className="bg-accent-yellow text-black px-4 py-2.5 text-center text-sm font-semibold rounded-full hover:bg-white transition-colors">{t.cta.client}</Link>
              <Link to="/contato?intent=partner" onClick={() => setMobileOpen(false)} className="bg-white text-black px-4 py-2.5 text-center text-sm font-semibold rounded-full hover:bg-accent-orange hover:text-white transition-colors">{t.cta.partner}</Link>
              <Link to="/cliente" onClick={() => setMobileOpen(false)} className="bg-accent-orange text-white px-4 py-2.5 text-center text-sm font-semibold rounded-full hover:bg-black hover:text-accent-orange border-2 border-accent-orange transition-colors">{t.cta.clientArea || "Área do Cliente"}</Link>
              <div className="flex gap-2 mt-1 justify-center">
                {["pt","en","es"].map((l) => (
                  <button key={l} onClick={() => changeLang(l)} className={`px-2.5 py-0.5 text-[10px] rounded-full border transition-colors ${lang===l?"bg-white text-black":"border-white/30 hover:bg-white/10"}`}>{l.toUpperCase()}</button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
